
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface RrwebSession {
  id: string;
  user_id: string;
  name?: string;
  description?: string;
  duration_ms?: number;
  initial_url?: string;
  viewport_width?: number;
  viewport_height?: number;
  events_storage_path: string;
  original_audio_storage_path?: string;
  generated_script?: string;
  script_generated_at?: string;
  created_at: string;
}

export const useRrwebSessions = () => {
  return useQuery({
    queryKey: ['rrweb-sessions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rrweb_sessions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching rrweb sessions:', error);
        throw error;
      }

      return data as RrwebSession[];
    },
  });
};

export const useCreateRrwebSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionData: {
      name?: string;
      description?: string;
      duration_ms?: number;
      initial_url?: string;
      viewport_width?: number;
      viewport_height?: number;
      events_storage_path: string;
      original_audio_storage_path?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('rrweb_sessions')
        .insert([
          {
            ...sessionData,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating rrweb session:', error);
        throw error;
      }

      return data as RrwebSession;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rrweb-sessions'] });
      toast.success('Recording session created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create rrweb session:', error);
      toast.error('Failed to create recording session');
    },
  });
};
